/**
 * @fileoverview Dialog de busca global com resultados agrupados por tipo.
 *
 * Busca com debounce de 250ms em leads e clientes.
 * Funcionalidades:
 * - Buscas recentes salvas no localStorage
 * - Navegação por teclado (setas + Enter)
 * - Resultados agrupados com ícones e cores por tipo
 * - Proteção contra resultados stale (race condition)
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, FileText, Users, Clock, ArrowRight, Sparkles, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useStore } from '@/store/useStore';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

interface SearchResult {
  id: string;
  title: string;
  /** Vem de colunas nullable (email/company), então aceita null. */
  subtitle?: string | null;
  type: 'lead' | 'client';
  link: string;
  icon: typeof FileText;
}

// `pluralLabel` é usado nos cabeçalhos dos grupos para evitar pluralização ad-hoc.
const TYPE_CONFIG = {
  lead:   { icon: Users, label: 'Lead',    pluralLabel: 'Leads',    color: 'text-success bg-success/10' },
  client: { icon: Users, label: 'Cliente', pluralLabel: 'Clientes', color: 'text-warning bg-warning/10' },
};

const RECENT_KEY = 'bl-recent-searches';

export default function GlobalSearchDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { activeProjectId } = useStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
  });

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query.trim() || !activeProjectId) { setResults([]); return; }

    const currentQuery = query;
    const timer = setTimeout(async () => {
      setLoading(true);
      const q = `%${currentQuery}%`;
      
      const [leads, clients] = await Promise.all([
        supabase.from('leads').select('id, contact_name, company, email').eq('project_id', activeProjectId).is('deleted_at', null).or(`contact_name.ilike.${q},company.ilike.${q},email.ilike.${q}`).limit(5),
        supabase.from('clients').select('id, company, contact_name').eq('project_id', activeProjectId).is('deleted_at', null).or(`company.ilike.${q},contact_name.ilike.${q}`).limit(3),
      ]);

      const allResults: SearchResult[] = [
        ...(leads.data || []).map(l => ({
          id: l.id, title: l.contact_name || l.company || 'Lead', subtitle: l.email || l.company,
          type: 'lead' as const, link: `/pipeline`, icon: Users,
        })),
        ...(clients.data || []).map(c => ({
          id: c.id, title: c.company || c.contact_name || 'Cliente', subtitle: c.contact_name,
          type: 'client' as const, link: `/clientes`, icon: Users,
        })),
      ];

      // Only update if this is still the latest query (prevents stale results)
      if (currentQuery === query) {
        setResults(allResults);
        setSelectedIdx(0);
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [query, activeProjectId]);

  const handleSelect = (result: SearchResult) => {
    // Save to recent
    const updated = [query, ...recentSearches.filter(r => r !== query)].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    setRecentSearches(updated);

    navigate(result.link);
    onOpenChange(false);
  };

  const handleClearRecents = useCallback(() => {
    try { localStorage.removeItem(RECENT_KEY); } catch { /* empty */ }
    setRecentSearches([]);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(p => Math.min(p + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(p => Math.max(p - 1, 0)); }
    if (e.key === 'Enter' && results[selectedIdx]) { handleSelect(results[selectedIdx]); }
  };

  // Group results by type
  const grouped = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {};
    results.forEach(r => {
      if (!groups[r.type]) groups[r.type] = [];
      groups[r.type].push(r);
    });
    return groups;
  }, [results]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Busca global</DialogTitle>
        <DialogDescription className="sr-only">Pesquise leads e clientes do projeto CRM</DialogDescription>
        {/* Search Input */}
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar leads ou clientes..."
            className="border-0 shadow-none focus-visible:ring-0 text-sm h-auto p-0"
          />
          {loading && <div className="h-4 w-4 border-2 border-secondary border-t-transparent rounded-full animate-spin shrink-0" />}
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto scrollbar-thin">
          {query.trim() === '' ? (
            <div className="p-4">
              {recentSearches.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Recentes</p>
                    <button
                      type="button"
                      onClick={handleClearRecents}
                      className="text-[10px] text-muted-foreground hover:text-destructive inline-flex items-center gap-1 transition-colors"
                      title="Limpar histórico de buscas"
                    >
                      <Trash2 className="h-3 w-3" /> Limpar
                    </button>
                  </div>
                  {recentSearches.map((s) => (
                    <button
                      key={s}
                      className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md hover:bg-muted text-xs text-muted-foreground"
                      onClick={() => setQuery(s)}
                    >
                      <Clock className="h-3 w-3" />
                      <span>{s}</span>
                    </button>
                  ))}
                </>
              )}
              {recentSearches.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Sparkles className="h-6 w-6 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Digite para buscar no projeto</p>
                  <p className="text-[10px] mt-1">Leads e clientes</p>
                </div>
              )}
            </div>
          ) : query.trim().length === 1 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="h-6 w-6 mx-auto mb-2 opacity-30" />
              <p className="text-xs">Digite ao menos 2 letras para buscar</p>
            </div>
          ) : results.length === 0 && !loading ? (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="h-6 w-6 mx-auto mb-2 opacity-30" />
              <p className="text-xs">Nenhum resultado para "{query}"</p>
            </div>
          ) : (
            Object.entries(grouped).map(([type, items]) => {
              const config = TYPE_CONFIG[type as keyof typeof TYPE_CONFIG];
              return (
                <div key={type}>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-4 py-1.5 bg-muted/30 flex items-center gap-2">
                    <span>{config.pluralLabel}</span>
                    <span className="text-muted-foreground/60 font-normal normal-case tracking-normal">({items.length})</span>
                  </p>
                  {items.map((result, i) => {
                    const globalIdx = results.indexOf(result);
                    return (
                      <button
                        key={result.id}
                        className={cn(
                          'flex items-center gap-3 w-full px-4 py-2 text-left transition-colors',
                          globalIdx === selectedIdx ? 'bg-muted' : 'hover:bg-muted/50'
                        )}
                        onClick={() => handleSelect(result)}
                        onMouseEnter={() => setSelectedIdx(globalIdx)}
                      >
                        <div className={cn('h-7 w-7 rounded-lg flex items-center justify-center shrink-0', config.color)}>
                          <config.icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">{result.title}</p>
                          {result.subtitle && (
                            <p className="text-[10px] text-muted-foreground truncate">{result.subtitle}</p>
                          )}
                        </div>
                        <ArrowRight className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-4 py-2 border-t bg-muted/30 text-[10px] text-muted-foreground">
          <span><kbd className="px-1 py-0.5 rounded bg-muted border text-[9px]">↑↓</kbd> navegar</span>
          <span><kbd className="px-1 py-0.5 rounded bg-muted border text-[9px]">↵</kbd> abrir</span>
          <span><kbd className="px-1 py-0.5 rounded bg-muted border text-[9px]">esc</kbd> fechar</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
